// Monthly Summary Repository — Load/Retrieve merge helpers (ALPACA-LIMS parity,
// MSRController.RetrieveMSRAction_Execute). Extracted from MsrDetailDialog so the
// merge rules are unit-testable in isolation.
//
// Load/Retrieve does NOT blindly replace the grid: it keeps every row still
// relevant to the current Year/Month/Category, drops rows that no longer match,
// refreshes the values of rows the fresh import also contains, and appends only
// the newly-discovered samples (green) — so a re-Load on an already-saved record
// surfaces new results without losing what is already shown.

/** The structural subset of a sample row the merge/prune helpers rely on. The
 *  full grid row (SampleRow) carries more fields; the generic helpers preserve
 *  them (runtime spread) while typing against this minimal shape. */
export interface MsrMergeRow {
  sampleLogInOid?: string;
  projectId?: string;
  siteName?: string;
  address?: string;
  date?: string;
  time?: string;
  freeChlorine?: string;
  totalChlorine?: string;
  monochloramine?: string;
  freeAmmonia?: string;
  totalColiform?: string;
  ecoli?: string;
  nitrite?: string;
  nitrate?: string;
  disinfectant?: string;
  collector?: string;
  sampleType?: string;
  isNew?: boolean;
  /** A saved row whose values the latest Load changed (client-only, orange row). */
  isModified?: boolean;
}

/** The measured / derived columns a Load refreshes on an already-saved row. */
const MSR_VALUE_FIELDS = [
  'freeChlorine', 'totalChlorine', 'monochloramine', 'freeAmmonia',
  'totalColiform', 'ecoli', 'nitrite', 'nitrate', 'disinfectant', 'collector',
] as const;

const MSR_EMPTY_OID = '00000000-0000-0000-0000-000000000000';

/** Drop rows that no longer match the selected Year / Month / Category (plain
 *  string slicing avoids any browser-timezone shift on the lab-local date —
 *  CLAUDE.md §14b.6). Rows without a date are always kept. */
export function pruneMismatchedMsrRows<T extends MsrMergeRow>(
  rows: T[], year: string, month: string, categoryName: string): T[] {
  return rows.filter((r) => {
    if (!r.date) return true;
    const [y, m] = r.date.slice(0, 10).split('-');
    if (y !== year) return false;
    if (String(Number(m) - 1) !== month) return false;
    if (categoryName && r.sampleType !== categoryName) return false;
    return true;
  });
}

/** Identity of a sample row for merge. Prefer the DB link (DB-retrieve path); but
 *  EXCEL-imported rows have no SampleLogIn, so their sampleLogInOid is Guid.Empty
 *  and can't tell two rows apart — for those we key on the BUSINESS tuple
 *  (Run, Site, Address, Date, Time), exactly as ALPACA does
 *  (MSRController.RetrieveMSRAction_Execute ~line 1346). Keying Excel rows on the
 *  empty oid was the bug that made every freshly-imported row look like a
 *  duplicate of the already-saved rows ("No new samples"). */
export function msrRowKey(r: MsrMergeRow): string {
  const oid = (r.sampleLogInOid ?? '').toLowerCase();
  if (oid && oid !== MSR_EMPTY_OID) return `oid${oid}`;
  return ['k', r.projectId ?? '', r.siteName ?? '', r.address ?? '',
    (r.date ?? '').slice(0, 10), r.time ?? ''].map((s) => s.trim()).join('');
}

/** Merge, don't replace. A fresh row already shown REFRESHES that row's measured
 *  values (a re-import corrects results); a fresh row with a new key is APPENDED
 *  and tagged isNew (green). Kept rows the fresh set doesn't mention are left as
 *  they were. */
export function mergeMsrRows<T extends MsrMergeRow>(kept: T[], fresh: T[]): T[] {
  const freshByKey = new Map(fresh.map((r) => [msrRowKey(r), r]));
  const keptKeys = new Set(kept.map(msrRowKey));
  const updated = kept.map((k) => {
    const f = freshByKey.get(msrRowKey(k));
    if (!f) return k;
    const next = { ...k,
      freeChlorine:   f.freeChlorine,
      totalChlorine:  f.totalChlorine,
      monochloramine: f.monochloramine,
      freeAmmonia:    f.freeAmmonia,
      // Results that arrive after the first Load (e.g. Colilert Total Coliform /
      // E. coli) must reach an already-saved row; a blank fresh value never
      // wipes what the row already holds.
      totalColiform:  f.totalColiform || k.totalColiform,
      ecoli:          f.ecoli         || k.ecoli,
      nitrite:        f.nitrite       || k.nitrite,
      nitrate:        f.nitrate       || k.nitrate,
      disinfectant:   f.disinfectant ?? k.disinfectant,
      collector:      f.collector    ?? k.collector };
    // ALPACA MonthlySummaryRepositorySamples.IsModified: a saved row whose values
    // this Load actually changed is flagged (orange row); an unchanged row is not.
    const changed = MSR_VALUE_FIELDS.some((c) => (next[c] ?? '') !== (k[c] ?? ''));
    return changed ? { ...next, isModified: true } : next;
  });
  const appended = fresh
    .filter((r) => !keptKeys.has(msrRowKey(r)))
    .map((r) => ({ ...r, isNew: true }));
  return [...updated, ...appended];
}
